import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, of, Subscription, switchMap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { DoctorsService } from '../../../../services/doctors';
import { JobApplicationService } from '../../../../services/job-application';

@Component({
  selector: 'app-applyfordoctor',
  templateUrl: './applyfordoctor.component.html',
  styleUrl: './applyfordoctor.component.scss'
})
export class ApplyfordoctorComponent implements OnInit, OnDestroy {
  @Input() jobId: number | string | null = null;
  @Output() applicationSubmitted = new EventEmitter<any>();

  form!: FormGroup;

  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  certificateErrorMessage = '';

  selectedCv: File | null = null;
  selectedImage: File | null = null;
  selectedCertificates: File[] = [];

  currentStep = 0;
  readonly steps = [1, 2, 3];

  private readonly maxCvSize = 5 * 1024 * 1024;
  private readonly maxCertificateSize = 5 * 1024 * 1024;
  private readonly maxImageSize = 2 * 1024 * 1024;
  private readonly maxCertificatesCount = 20;

  private readonly allowedCertificateExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.pdf'
  ];

  private readonly subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private doctorsService: DoctorsService,
    private applicationService: JobApplicationService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.jobId = this.jobId || this.route.snapshot.paramMap.get('id');

    this.form = this.fb.group({
      name: ['', Validators.required],
      mobile: ['', [Validators.required, Validators.maxLength(30)]],
      email: ['', Validators.email],
      specialty: [''],

      residence_place: [''],
      birth_place: [''],
      birth_date: [''],

      has_phd: [false],
      phd_source: [''],
      phd_year: [null],

      has_master: [false],
      master_source: [''],
      master_year: [null],

      university: [''],
      graduation_year: [null, [Validators.min(1940), Validators.max(2100)]],
      fellowship: [''],
      classification_status: [''],

      ksa_experience: [false],
      transfer_of_sponsorship: [false],

      // موجود في الفورم لكنه مش ظاهر للمستخدم
      // وهيتبعت للباك دايمًا false / 0
      offer: [false],

      visa: [false],
      has_dataflow: [false],

      notes: ['']
    });

    this.setupDegreeFields();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.certificateErrorMessage = '';

    this.forceOfferFalse();

    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      this.errorMessage = this.isAr
        ? 'راجع البيانات المطلوبة قبل الإرسال.'
        : 'Please review the required fields before submitting.';
      return;
    }

    if (this.jobId && !this.selectedCv) {
      this.errorMessage = this.t('cvRequired');
      return;
    }

    if (!this.form.value.mobile?.trim()) {
      this.errorMessage = this.t('phoneRequiredForCv');
      return;
    }

    if (this.selectedCertificates.length > this.maxCertificatesCount) {
      this.certificateErrorMessage = this.isAr
        ? 'الحد الأقصى للشهادات هو 20 ملف.'
        : 'Maximum certificates count is 20 files.';
      return;
    }

    this.isSubmitting = true;

    this.doctorsService
      .create(this.buildDoctorFormData())
      .pipe(
        switchMap((doctorResponse: any) => {
          if (!this.jobId) {
            return of(doctorResponse);
          }

          const doctorId = doctorResponse?.data?.id || doctorResponse?.id;

          if (!doctorId) {
            throw new Error('Doctor profile was created without an ID.');
          }

          return this.applicationService.publicApplyToJob({
            job_id: this.jobId,
            doctor_id: doctorId,
            applicant_name: this.form.value.name.trim(),
            applicant_phone: this.form.value.mobile.trim(),
            applicant_email: this.form.value.email?.trim(),
            cv: this.selectedCv
          });
        }),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: (applicationResponse: any) =>
          this.completeApplication(applicationResponse),
        error: (error: any) => this.showRequestError(error)
      });
  }

  onCvChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    if (!file) return;

    const name = file.name.toLowerCase();
    const validType = ['.pdf', '.doc', '.docx'].some(extension =>
      name.endsWith(extension)
    );

    if (!validType) {
      this.selectedCv = null;
      this.errorMessage = this.t('invalidCv');
      return;
    }

    if (file.size > this.maxCvSize) {
      this.selectedCv = null;
      this.errorMessage = this.t('cvTooLarge');
      return;
    }

    this.errorMessage = '';
    this.selectedCv = file;
  }

  removeCv(): void {
    this.selectedCv = null;
  }

  onImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.selectedImage = null;
      this.errorMessage = this.isAr
        ? 'برجاء اختيار صورة صحيحة.'
        : 'Please choose a valid image.';
      return;
    }

    if (file.size > this.maxImageSize) {
      this.selectedImage = null;
      this.errorMessage = this.isAr
        ? 'حجم الصورة يجب ألا يزيد عن 2 ميجا.'
        : 'Image size must not exceed 2MB.';
      return;
    }

    this.errorMessage = '';
    this.selectedImage = file;
  }

  onCertificatesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';

    this.certificateErrorMessage = '';
    this.errorMessage = '';

    if (!files.length) return;

    const totalAfterAdd = this.selectedCertificates.length + files.length;

    if (totalAfterAdd > this.maxCertificatesCount) {
      this.certificateErrorMessage = this.isAr
        ? `لا يمكن رفع أكثر من ${this.maxCertificatesCount} ملف.`
        : `You cannot upload more than ${this.maxCertificatesCount} files.`;
      return;
    }

    for (const file of files) {
      if (!this.isValidCertificateFile(file)) {
        this.certificateErrorMessage = this.isAr
          ? 'الملفات المسموحة: صور JPG/PNG/WEBP أو PDF فقط.'
          : 'Allowed files: JPG/PNG/WEBP images or PDF only.';
        return;
      }

      if (file.size > this.maxCertificateSize) {
        this.certificateErrorMessage = this.isAr
          ? 'حجم كل شهادة يجب ألا يزيد عن 5 ميجا.'
          : 'Each certificate file must not exceed 5MB.';
        return;
      }
    }

    this.selectedCertificates = [...this.selectedCertificates, ...files];
  }

  removeCertificate(index: number): void {
    this.selectedCertificates = this.selectedCertificates.filter(
      (_, i) => i !== index
    );
    this.certificateErrorMessage = '';
  }

  clearCertificates(): void {
    this.selectedCertificates = [];
    this.certificateErrorMessage = '';
  }

  trackByFile(index: number, file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}-${index}`;
  }

  goBack(): void {
    this.router.navigate(['/jobs']);
  }

  nextStep(): void {
    this.errorMessage = '';

    if (this.currentStep === 0) {
      const firstStepControls = this.getFirstStepControls();

      const invalidControl = firstStepControls.find(
        controlName => this.form.get(controlName)?.invalid
      );

      if (!invalidControl) {
        this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      firstStepControls.forEach(controlName => {
        this.form.get(controlName)?.markAsTouched();
      });

      this.errorMessage = this.getFirstStepErrorMessage(invalidControl);
      return;
    }

    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  previousStep(): void {
    this.errorMessage = '';
    this.currentStep = Math.max(this.currentStep - 1, 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getStepLabel(index: number): string {
    const labels = [
      this.isAr ? 'البيانات' : 'Data',
      this.isAr ? 'الصورة الشخصية' : 'Profile photo',
      this.isAr ? 'السيرة الذاتية والشهادات' : 'CV & certificates'
    ];

    return labels[index];
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control?.invalid && (control.dirty || control.touched));
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang().startsWith('ar');
  }

  private setupDegreeFields(): void {
    this.updateDegreeControls(
      'phd_source',
      'phd_year',
      Boolean(this.form.get('has_phd')?.value)
    );

    this.updateDegreeControls(
      'master_source',
      'master_year',
      Boolean(this.form.get('has_master')?.value)
    );

    this.subscriptions.add(
      this.form.get('has_phd')?.valueChanges.subscribe(value => {
        this.updateDegreeControls('phd_source', 'phd_year', Boolean(value));
      })
    );

    this.subscriptions.add(
      this.form.get('has_master')?.valueChanges.subscribe(value => {
        this.updateDegreeControls('master_source', 'master_year', Boolean(value));
      })
    );
  }

  private updateDegreeControls(
    sourceControlName: string,
    yearControlName: string,
    checked: boolean
  ): void {
    const sourceControl = this.form.get(sourceControlName);
    const yearControl = this.form.get(yearControlName);

    if (!sourceControl || !yearControl) return;

    if (checked) {
      sourceControl.setValidators([Validators.required]);
      yearControl.setValidators([
        Validators.required,
        Validators.min(1940),
        Validators.max(2100)
      ]);
    } else {
      sourceControl.clearValidators();
      yearControl.clearValidators();

      sourceControl.setValue('', { emitEvent: false });
      yearControl.setValue(null, { emitEvent: false });
    }

    sourceControl.updateValueAndValidity({ emitEvent: false });
    yearControl.updateValueAndValidity({ emitEvent: false });
  }

  private getFirstStepControls(): string[] {
    const controls = [
      'name',
      'mobile',
      'email',
      'graduation_year'
    ];

    if (this.form.get('has_phd')?.value) {
      controls.push('phd_source', 'phd_year');
    }

    if (this.form.get('has_master')?.value) {
      controls.push('master_source', 'master_year');
    }

    return controls;
  }

  private getFirstStepErrorMessage(controlName: string): string {
    if (controlName === 'name') {
      return this.t('nameRequired');
    }

    if (controlName === 'mobile') {
      return this.t('phoneRequiredForCv');
    }

    if (controlName === 'email') {
      return this.t('emailInvalid');
    }

    if (controlName === 'phd_source') {
      return this.isAr ? 'مصدر الدكتوراه مطلوب.' : 'PhD source is required.';
    }

    if (controlName === 'phd_year') {
      return this.isAr
        ? 'أدخل سنة دكتوراه صحيحة بين 1940 و2100.'
        : 'Enter a valid PhD year between 1940 and 2100.';
    }

    if (controlName === 'master_source') {
      return this.isAr ? 'مصدر الماجستير مطلوب.' : 'Master source is required.';
    }

    if (controlName === 'master_year') {
      return this.isAr
        ? 'أدخل سنة ماجستير صحيحة بين 1940 و2100.'
        : 'Enter a valid master year between 1940 and 2100.';
    }

    return this.isAr
      ? 'أدخل سنة صحيحة بين 1940 و2100.'
      : 'Enter a valid year between 1940 and 2100.';
  }

  private buildDoctorFormData(): FormData {
    const formData = new FormData();

    this.forceOfferFalse();

    const values = {
      ...this.form.getRawValue(),
      offer: false
    };

    Object.entries(values).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    });

    // تأكيد إضافي إن offer دايمًا false حتى لو حصل أي تغيير في الفورم
    formData.set('offer', '0');

    if (this.selectedImage) {
      formData.append('image', this.selectedImage);
    }

    this.selectedCertificates.forEach(file => {
      formData.append('certificates[]', file);
    });

    const username =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('username')
        : null;

    formData.append('add_by', username || 'public-application');
    formData.append('is_doctor', '1');

    return formData;
  }

  private forceOfferFalse(): void {
    this.form.get('offer')?.setValue(false, { emitEvent: false });
  }

  private isValidCertificateFile(file: File): boolean {
    const fileName = file.name.toLowerCase();

    return this.allowedCertificateExtensions.some(extension =>
      fileName.endsWith(extension)
    );
  }

  private completeApplication(applicationResponse: any): void {
    this.successMessage = this.t('publicApplySuccess');
    this.applicationSubmitted.emit(applicationResponse);

    setTimeout(() => {
      this.router.navigate(['/jobs']);
    }, 1200);
  }

  private showRequestError(error: any): void {
    const validationErrors = error?.error?.errors;

    if (validationErrors) {
      const firstKey = Object.keys(validationErrors)[0];
      this.errorMessage =
        validationErrors[firstKey]?.[0] || this.t('requestFailed');
      return;
    }

    this.errorMessage =
      error?.error?.message ||
      error?.error?.error ||
      error?.message ||
      this.t('requestFailed');
  }

  private t(key: string): string {
    return this.transloco.translate(`atw.doctorApply.${key}`);
  }
}